import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Alert, Button } from 'react-bootstrap'
import Select from 'react-select'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'
import 'survey-core/defaultV2.min.css'
import './index.scss'

import httpClient from 'utility/httpClient.util'
import { useLockEdit } from 'contexts/PatientLockEditProvider'
import { useTimeFormat } from 'contexts/TimeFormatContext'
import { unwrapList, getApiErrorMessage } from '../PatientRegister/api'

const CODE = 'patient-pdpa'

const PatientPdpaForm = forwardRef(({
  form = {},
  readOnly = false,
  showHistory = false,
  hideFooter = false,
  onSaveCallback = () => {},
}, ref) => {
  const { isLocked } = useLockEdit() || {}
  const { formatDateTime } = useTimeFormat()
  const [definition, setDefinition] = useState(null)
  const [records, setRecords] = useState([])
  const [selectedId, setSelectedId] = useState(form.patientAssessmentId || '')
  const [record, setRecord] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [ready, setReady] = useState(false)
  const [reload, setReload] = useState(0)
  const dirtyRef = useRef(false)
  const savingRef = useRef(false)
  const selectLatestRef = useRef(true)
  const disabled = readOnly || isLocked || Boolean(selectedId)

  useEffect(() => {
    selectLatestRef.current = true
    setSelectedId(form.patientAssessmentId || '')
  }, [form.hn, form.patientAssessmentId])

  useEffect(() => {
    let active = true
    if (!form.hn) return () => { active = false }
    setLoading(true)
    setReady(false)
    setIsDirty(false)
    setError('')
    setRecord(null)
    dirtyRef.current = false
    const load = async () => {
      try {
        const [definitionResponse, historyResponse, recordResponse] = await Promise.all([
          selectedId ? Promise.resolve(null) : httpClient.get(`/designer/form-assessment/search?code=${CODE}`, { cache: false }),
          showHistory
            ? httpClient.get(`/clinical/patient-assessment?hn=${encodeURIComponent(form.hn)}&categoryCode=PDPA&include=updatedBy&page=1&perPage=1000`, { cache: false })
            : Promise.resolve(null),
          selectedId
            ? httpClient.get(`/clinical/patient-assessment/${encodeURIComponent(selectedId)}`, { cache: false })
            : Promise.resolve(null),
        ])
        if (!active) return
        const currentDefinition = unwrapList(definitionResponse)[0]
        const selectedRecord = unwrapList(recordResponse)[0]
        const history = unwrapList(historyResponse).filter((item) => item.assessmentCode === CODE)
        if (showHistory && selectLatestRef.current) {
          selectLatestRef.current = false
          if (!selectedId && history.length) {
            setSelectedId(history[0].patientAssessmentId)
            return
          }
        }
        if (selectedId && (!selectedRecord || selectedRecord.assessmentCode !== CODE || String(selectedRecord.hn) !== String(form.hn))) {
          throw new Error('ไม่พบข้อมูล PDPA ของผู้ป่วยรายนี้')
        }
        if (!selectedId && !currentDefinition?.jsonData) throw new Error('ไม่พบแบบฟอร์ม PDPA')
        if (selectedId && !selectedRecord?.resultSummary?.formSnapshot) throw new Error('รายการนี้ไม่มีสำเนาแบบฟอร์มที่ใช้บันทึก')
        setDefinition(currentDefinition)
        setRecords(history)
        setRecord(selectedRecord || null)
        setReady(true)
      } catch (loadError) {
        if (active) setError(getApiErrorMessage(loadError))
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => { active = false }
  }, [form.hn, selectedId, showHistory, reload])

  const survey = useMemo(() => {
    const json = record?.resultSummary?.formSnapshot || definition?.jsonData
    if (!json || (selectedId && !record)) return null
    const model = new Model(json)
    model.locale = 'th'
    model.showNavigationButtons = false
    model.showCompletedPage = false
    model.mode = disabled ? 'display' : 'edit'
    model.data = record?.jsonData || {}
    model.onValueChanged.add(() => { dirtyRef.current = true; setIsDirty(true) })
    return model
  }, [definition, record, selectedId, disabled])

  const save = async () => {
    if (disabled || !survey || !form.hn || loading || !ready || savingRef.current) return { success: false }
    if (!survey.validate()) return { success: false }
    savingRef.current = true
    setSaving(true)
    setError('')
    setSaved(false)
    try {
      const jsonData = { ...survey.data }
      const summary = survey.getAllQuestions().filter((question) => question.isVisible).map((question) => ({
        title: question.title,
        name: question.name,
        type: question.getType(),
        value: question.displayValue,
        valueItem: question.value,
      }))
      const response = await httpClient.post('/clinical/patient-assessment', {
        hn: form.hn,
        vn: null,
        an: null,
        assessmentCode: CODE,
        assessmentName: definition.name,
        formAssessmentId: definition.formAssessmentId,
        jsonData,
        resultSummary: { summary, remark: [], formSnapshot: definition.jsonData },
      })
      const assessment = unwrapList(response)[0]
      if (!assessment?.patientAssessmentId) throw new Error('ระบบไม่ได้ส่งเลขที่รายการ PDPA กลับมา กรุณาตรวจสอบประวัติก่อนบันทึกซ้ำ')
      dirtyRef.current = false
      setIsDirty(false)
      setSelectedId(assessment.patientAssessmentId)
      setSaved(true)
      onSaveCallback(response, { assessment, jsonData, surveyJson: definition.jsonData })
      return { success: true, result: response }
    } catch (saveError) {
      setError(getApiErrorMessage(saveError))
      return { success: false }
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }

  useImperativeHandle(ref, () => ({ isDirty: () => dirtyRef.current, save }))

  const historyOptions = records.map((item) => ({
    value: item.patientAssessmentId,
    label: `${formatDateTime(item.resultSummary?.recordedAt || item.updatedAt)} — ${[item.updatedBy?.firstName, item.updatedBy?.lastName].filter(Boolean).join(' ') || 'ไม่ระบุผู้บันทึก'}`,
  }))

  if (!form.hn) return <Alert variant="info">กรุณาบันทึกข้อมูลผู้ป่วยให้มี HN ก่อนบันทึก PDPA</Alert>

  return (
    <React.Fragment>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <strong>PDPA — ความยินยอมของผู้ป่วย</strong>
        {!readOnly && !isLocked && selectedId && (
          <Button variant="outline-primary" disabled={loading || saving} onClick={() => { setSelectedId(''); setSaved(false) }}>
            บันทึกความยินยอมครั้งใหม่
          </Button>
        )}
      </div>
      {showHistory && historyOptions.length > 0 && (
        <div className="mb-3">
          <label htmlFor="patient-pdpa-history">ประวัติการบันทึก</label>
          <Select inputId="patient-pdpa-history" options={historyOptions} value={historyOptions.find((item) => String(item.value) === String(selectedId)) || null} placeholder="เลือกประวัติการบันทึก" isDisabled={loading || saving || isDirty} onChange={(option) => { setSelectedId(option.value); setSaved(false) }} />
        </div>
      )}
      {saved && <Alert variant="success">บันทึก PDPA แล้ว</Alert>}
      {error && <Alert variant="danger">{error} <Button variant="link" onClick={() => setReload((value) => value + 1)}>โหลดใหม่</Button></Alert>}
      {loading ? <div className="text-muted">กำลังโหลด PDPA...</div> : ready && survey && (
        <div className="assessment-form assessment-form--compact">
          <div className="assessment-form__survey"><Survey model={survey} /></div>
        </div>
      )}
      {!hideFooter && !disabled && (
        <Button className="mt-3" variant="outline-success" disabled={loading || saving || !survey || !ready} onClick={save}>
          {saving ? 'กำลังบันทึก...' : 'บันทึก PDPA'}
        </Button>
      )}
    </React.Fragment>
  )
})

export default PatientPdpaForm
